import { RootState } from 'modules/store';
import { createSlice } from '@reduxjs/toolkit';
import { AvsConfiguration, AvsState, VerificationResponse } from './avs.types';
import {
  getAvsConfigurations,
  getBulkVerificationResult,
  getBulkVerificationResultData,
  getIbanVerificationResult,
  getVerificationRecords,
  updateAvsConfigurations,
  uploadBulkVerification,
  verifyAccount,
  requestServiceAccess,
} from './avs.actions';

const initialState: AvsState = {
  configurations: [],
  updatedConfigurations: [],
  verificationState: null,
  verificationRecords: null,
  verificationResultDetail: null,
  bulkVerificationResponse: null,
  bulkVerificationResult: null,
  bulkVerificationResultData: null,
  error: '',
  isFetchingConfigurations: false,
  isFetchingVerificationRecords: false,
  isFetchingVerificationResult: false,
  isFetchingVerificationResultData: false,
  isSubmitting: false,
  isVerifying: false,
};

const avsSlice = createSlice({
  name: 'avs',
  initialState,
  reducers: {
    setUpdateConfigurations: (state, { payload }) => {
      state.updatedConfigurations = payload;
    },
    resetVerificationState: state => {
      state.verificationState = null;
      state.isVerifying = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(getAvsConfigurations.pending, state => {
      state.isFetchingConfigurations = true;
    });
    builder.addCase(getAvsConfigurations.fulfilled, (state, { payload }) => {
      state.isFetchingConfigurations = false;
      state.configurations = payload.map((configuration: AvsConfiguration) => ({
        ...configuration,
        id: configuration.bank_code,
        isSelectable: false,
      }));
    });
    builder.addCase(getAvsConfigurations.rejected, state => {
      state.isFetchingConfigurations = false;
      state.error = 'Failed to load Account Verification configurations';
    });

    builder.addCase(updateAvsConfigurations.pending, state => {
      state.isSubmitting = true;
    });
    builder.addCase(updateAvsConfigurations.fulfilled, state => {
      state.isSubmitting = false;
      state.updatedConfigurations = [];
    });
    builder.addCase(updateAvsConfigurations.rejected, state => {
      state.isSubmitting = false;
    });

    builder.addCase(verifyAccount.pending, state => {
      state.isVerifying = true;
    });
    builder.addCase(verifyAccount.fulfilled, (state, { payload }) => {
      state.isVerifying = false;
      state.verificationState = payload;
    });
    builder.addCase(verifyAccount.rejected, (state, { payload }) => {
      state.isVerifying = false;
      state.verificationState = payload as VerificationResponse<any>;
    });
    builder.addCase(uploadBulkVerification.pending, state => {
      state.bulkVerificationResponse = null;
      state.isFetchingVerificationResult = true;
    });
    builder.addCase(uploadBulkVerification.fulfilled, (state, { payload }) => {
      state.bulkVerificationResponse =
        payload as unknown as VerificationResponse<never>;
      state.isFetchingVerificationResult = false;
    });
    builder.addCase(uploadBulkVerification.rejected, (state, { payload }) => {
      state.bulkVerificationResponse = payload as VerificationResponse<never>;
      state.isFetchingVerificationResult = false;
    });
    builder.addCase(getVerificationRecords.pending, state => {
      state.isFetchingVerificationRecords = true;
    });
    builder.addCase(getVerificationRecords.fulfilled, (state, { payload }) => {
      state.isFetchingVerificationRecords = false;
      state.verificationRecords = payload;
    });
    builder.addCase(getVerificationRecords.rejected, state => {
      state.isFetchingVerificationRecords = false;
    });
    builder.addCase(getBulkVerificationResult.pending, state => {
      state.isFetchingVerificationResult = true;
    });
    builder.addCase(
      getBulkVerificationResult.fulfilled,
      (state, { payload }) => {
        state.isFetchingVerificationResult = false;
        state.bulkVerificationResult = payload;
      },
    );
    builder.addCase(getBulkVerificationResult.rejected, state => {
      state.isFetchingVerificationResult = false;
    });
    builder.addCase(
      getBulkVerificationResultData.pending,
      (state, { meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingVerificationResultData = true;
        }
      },
    );
    builder.addCase(
      getBulkVerificationResultData.fulfilled,
      (state, { payload, meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingVerificationResultData = false;
          state.bulkVerificationResultData = payload;
        }
      },
    );
    builder.addCase(getBulkVerificationResultData.rejected, state => {
      state.isFetchingVerificationResultData = false;
    });
    builder.addCase(getIbanVerificationResult.pending, (state, { meta }) => {
      if (!meta.arg.isCsvFile) {
        state.isFetchingVerificationResult = true;
      }
    });
    builder.addCase(
      getIbanVerificationResult.fulfilled,
      (state, { payload, meta }) => {
        if (!meta.arg.isCsvFile) {
          state.isFetchingVerificationResult = false;
          state.verificationResultDetail = payload;
        }
      },
    );
    builder.addCase(getIbanVerificationResult.rejected, state => {
      state.isFetchingVerificationResult = false;
    });
    builder.addCase(requestServiceAccess.pending, state => {
      state.isSubmitting = true;
    });
    builder.addCase(requestServiceAccess.fulfilled, state => {
      state.isSubmitting = false;
    });
    builder.addCase(requestServiceAccess.rejected, (state, { payload }) => {
      state.isSubmitting = false;
      state.error = 'Failed to request service access';
    });
  },
});

export default avsSlice.reducer;
export const selectAvsState = (state: RootState) => state.root.avs;
export const { setUpdateConfigurations, resetVerificationState } =
  avsSlice.actions;
